import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Users, Flame } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * MessagesBell — header CTA with a first-time onboarding popover.
 *
 * iter150 shipped the pulse + label + tooltip. iter152 adds a proper
 * popover that opens automatically for logged-in users who haven't
 * clicked into Messages yet, explaining what the feature does.
 * Dismissed forever once they either open the messages page OR tap
 * the popover's close button.
 */
const DISCOVERED_KEY = "messagesBell.discovered.v1";
const POPOVER_SEEN_KEY = "messagesBell.popover.v2";

export default function MessagesBell() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [discovered, setDiscovered] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(DISCOVERED_KEY) === "1";
  });
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/dms/unread-count");
        if (alive) setUnread(data.unread || 0);
      } catch { /* noop */ }
    };
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  // Open the intro popover once per user, after a short pause so it
  // doesn't clash with page-load animations.
  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(POPOVER_SEEN_KEY) === "1";
    if (seen) return;
    const t = setTimeout(() => setPopoverOpen(true), 1500);
    return () => clearTimeout(t);
  }, [user]);

  if (!user) return null;

  const dismissPopover = (markSeen = true) => {
    setPopoverOpen(false);
    if (markSeen) {
      try { localStorage.setItem(POPOVER_SEEN_KEY, "1"); } catch { /* noop */ }
    }
  };

  const markDiscovered = () => {
    dismissPopover();
    if (discovered) return;
    try { localStorage.setItem(DISCOVERED_KEY, "1"); } catch { /* noop */ }
    setDiscovered(true);
  };

  return (
    <div className="relative">
      <Link
        to="/messages"
        onClick={markDiscovered}
        className="relative inline-flex items-center gap-1.5 pl-1.5 pr-2 py-1.5 rounded-full hover:bg-[#fff4ee] transition-colors"
        title="Chat with other campers"
        aria-label="Chat with other campers"
        data-testid="header-messages-bell"
      >
        <span className="relative inline-flex items-center justify-center">
          {!discovered && (
            <span
              aria-hidden="true"
              className="absolute inset-0 -m-1 rounded-full bg-[#ff4500]/25 animate-ping"
            />
          )}
          <MessageCircle className="relative w-5 h-5 text-[#0e0e0e]" />
        </span>
        <span
          className="hidden sm:inline text-[11px] font-black uppercase tracking-wider text-[#0e0e0e]"
          data-testid="header-messages-label"
        >
          Chat
        </span>
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-[#ff4500] text-white text-[9px] font-bold px-1.5 rounded-full min-w-[16px] text-center leading-4"
            data-testid="header-messages-unread"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Link>

      {popoverOpen && (
        <>
          {/* backdrop — captures outside taps to dismiss */}
          <button
            type="button"
            aria-label="Dismiss chat intro"
            className="fixed inset-0 z-[9998] bg-transparent"
            onClick={() => dismissPopover()}
          />
          <div
            className="fixed right-3 top-14 sm:top-16 z-[9999] w-[280px] sm:w-[320px] max-w-[calc(100vw-1.5rem)] bg-white border hairline rounded-2xl shadow-xl p-4"
            role="dialog"
            aria-label="Chat with fellow campers"
            data-testid="messages-bell-popover"
          >
            {/* speech-bubble arrow removed — popover is now viewport-fixed rather than
                anchored to the bell (which sat past the right edge on desktop, causing
                the popover contents to clip). Users still see the direct connection
                because the popover slides in immediately below the header. */}
            <button
              type="button"
              onClick={() => dismissPopover()}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-[#fff4ee]"
              aria-label="Close"
              data-testid="messages-bell-popover-close"
            >
              <X className="w-3.5 h-3.5 text-[#666]" />
            </button>

            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#ff4500] text-white flex items-center justify-center shrink-0">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div className="font-black text-sm tracking-tight">
                Chat with fellow campers 👋
              </div>
            </div>

            <p className="text-xs text-[#444] leading-relaxed mb-3">
              Tap the <strong>Chat</strong> bubble anytime to:
            </p>

            <ul className="text-xs text-[#333] space-y-1.5 mb-4">
              <li className="flex items-start gap-2">
                <Users className="w-3.5 h-3.5 text-[#ff4500] shrink-0 mt-0.5" />
                <span><strong>Direct message</strong> any other camper for tips &amp; support <em className="text-[#ff4500] not-italic font-bold">(Pro)</em></span>
              </li>
              <li className="flex items-start gap-2">
                <Flame className="w-3.5 h-3.5 text-[#ff4500] shrink-0 mt-0.5" />
                <span><strong>Campfire chat room</strong> — one place to ask the whole community anything <em className="text-[#ff4500] not-italic font-bold">(read free, post with Pro)</em></span>
              </li>
            </ul>

            <Link
              to="/messages"
              onClick={markDiscovered}
              className="block w-full text-center px-3 py-2 rounded-full bg-[#ff4500] text-white text-xs font-black uppercase tracking-wider hover:bg-[#e63e00]"
              data-testid="messages-bell-popover-cta"
            >
              Show me →
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
